import { Grid, Switch as SwitchButton } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { HeaderContent } from '../../../components/HeaderContent';
import { Project } from '../../../domain/project/Project';
import { ProjectConstraints as ProjectConstraintsValueObject } from '../../../domain/project/projectConstraints/ProjectConstraints';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useUpdateProject } from '../../../hooks/project/useUpdateProject';
import { useAsUser } from '../../../hooks/session/useAsUser';
import { useRetrieveSupportDocs } from '../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import { DataTable } from '../../../react-components/data-table/DataTable';
import Text from '../../../react-components/Text';
import { getProjectFieldsConstraints } from './ProjectConstraintsFields';

interface ProjectConstraintsProps {
  project: Project;
}

export const ProjectConstraints = ({ project }: ProjectConstraintsProps) => {
  const { asUser } = useAsUser();
  const { data: user } = useRetrieveUser();
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { run: editProject } = useUpdateProject({ project });
  const { data: userCredentials } = useRetrieveUserCredentials();

  const [constraints, setConstraints] = useState<ProjectConstraintsValueObject>(project.constraints);

  useEffect(() => {
    if (project) {
      setConstraints(project.constraints);
    }
  }, [project]);

  return (
    <>
      <Grid
        container
        direction="row"
        wrap="nowrap"
        style={{
          padding: `11px 20px`,
          backgroundColor: RoutalPalette.neutral02n,
          borderBottom: `1px solid ${RoutalPalette.neutral10}`,
        }}
      >
        <HeaderContent
          titleIntlId="projects.settings.fields.constraints.title"
          article={{
            intlId: `projects.settings.fields.constraints.article`,
            link: retrieveDoc({ supportPage: `settingsFields`, language: user?.language.value }),
          }}
        />
      </Grid>

      <Grid
        container
        direction="column"
        style={{
          padding: `16px 22px`,
        }}
      >
        <DataTable
          noFooter
          docs={Object.keys(getProjectFieldsConstraints(userCredentials?.isStaff(asUser))).map((key) => ({
            doc: { id: key },
          }))}
          total={Object.keys(getProjectFieldsConstraints(userCredentials?.isStaff(asUser))).length}
          showCheckboxes={false}
          columns={[
            {
              width: `calc(100% - 135px)`,
              accessorName: `name`,
              name: <Text variant="body" intlId="projects.settings.fields.column.name" />,
              formatter: ({ doc }) => {
                const { id: setting } = doc;
                const constraint = (getProjectFieldsConstraints(userCredentials?.isStaff(asUser)) as any)[setting];
                return (
                  <Grid item container direction="row" alignItems="center">
                    <Grid item container style={{ width: `unset` }}>
                      <img
                        alt=""
                        src={constraint.iconSrc}
                        style={{ width: `12px`, height: `12px` }}
                        draggable="false"
                      />
                    </Grid>

                    <HSpacer small />

                    <Grid item>
                      {constraint.description ? (
                        <RoutalTooltip
                          id="field-description"
                          placement="top"
                          overlayStyle={{
                            maxWidth: `300px`,
                          }}
                          title={intl.formatMessage({
                            id: constraint.description,
                          })}
                        >
                          <Text variant="body" intlId={`projects.settings.fields.constraints.${setting}`} />
                        </RoutalTooltip>
                      ) : (
                        <Text variant="body" intlId={`projects.settings.fields.constraints.${setting}`} />
                      )}
                    </Grid>
                  </Grid>
                );
              },
            },
            {
              accessorName: `enabled`,
              align: `right`,
              name: <Text variant="body" intlId="enabled" />,
              formatter: ({ doc }) => {
                const { id: setting } = doc;
                return (
                  <Grid container>
                    <Grid item>
                      <SwitchButton
                        checked={(constraints.getValue() as unknown as Record<string, boolean>)[setting] || false}
                        onChange={(event: any) => {
                          const { checked } = event.target;
                          (project.constraints as any)[setting] = checked;
                          editProject({ constraints: project.constraints });
                        }}
                        color="primary"
                      />
                    </Grid>
                  </Grid>
                );
              },
            },
          ]}
        />
      </Grid>
    </>
  );
};
